import type { ThemeOptions } from '@mui/material';
export enum Currency {
  Aud = 'AUD',
  Cad = 'CAD',
  Chf = 'CHF',
  Cny = 'CNY',
  Dkk = 'DKK',
  Eur = 'EUR',
  Gbp = 'GBP',
  Isk = 'ISK',
  Jpy = 'JPY',
  Nok = 'NOK',
  Sek = 'SEK',
  Usd = 'USD',
}
export enum Locale {
  De = 'de-DE',
  En = 'en-GB',
  Is = 'is-IS',
}
export type AppConfiguration = {
  id: string;
  availableCurrencies: (Currency | string)[];
  availableLocales: Locale[];
  darkTheme?: AppTheme;
  defaultColorScheme?: 'light' | 'dark';
  defaultCurrency: Currency | string;
  defaultLocale: Locale | string;
  theme: AppTheme;
  name?: string;
  description?: string;
  manageAccountUrl?: string;
  footer: {
    rightText?: string;
    rightTextSubtitle?: string;
  };
};

/** How a layer — a toast or a modal — enters and leaves the screen. */
export type LayerAnimation = 'slideSide' | 'slideBottom' | 'fade';
export type AppTheme = {
  borderRadius?: 'none' | 'small' | 'large';
  modalAndLayerRadius?: 'none' | 'small' | 'medium' | 'large' | 'full';
  containerRadius?: 'none' | 'small' | 'medium' | 'large' | 'full';
  formItemRadius?: 'none' | 'small' | 'medium' | 'large' | 'full';
  buttonRadius?: 'none' | 'small' | 'medium' | 'large' | 'full';
  buttonRipple?: boolean;
  buttonTextTransform?: 'none' | 'uppercase' | 'capitalize';
  customStyles?: ThemeOptions;
  fontFaces?: string[];
  fontFamily?: string;
  faviconUrl?: string;
  logoUrl?: string;
  /** The logo's alt text. An empty string marks the logo as decorative. */
  logoAlt?: string;
  /** Draws switches the way iOS does: a filled pill track around the thumb. */
  iosSwitches?: boolean;
  /** `false` draws tooltips without an arrow; unset or `true` keeps it. */
  tooltipArrows?: boolean;
  /** `false` turns off component transitions; unset or `true` keeps them. */
  animations?: boolean;
  /** How toasts come in. Unset slides them in from the side. */
  toastAnimation?: LayerAnimation;
  /**
   * How toasts are drawn: `soft` on the container background with a coloured
   * icon, `hard` filled with the severity's colour. Unset is `soft`.
   */
  toastStyle?: 'soft' | 'hard';
  /** How modals come in. Unset fades them in. */
  modalAnimation?: LayerAnimation;
  /** How strong shadows are. Unset is `medium`, the design's own. */
  shadows?: 'off' | 'low' | 'medium' | 'high';
  /**
   * How the action bar sits at the bottom of a step from md up: `floating`
   * is an inset rounded bar pinned above the bottom of the screen; `fixed`
   * is docked at full width at the end of the content, so it is scrolled to
   * rather than always in view. Unset is `floating`. Below md it is always a
   * docked sheet pinned to the bottom of the screen.
   */
  actionBar?: 'floating' | 'fixed';
  /**
   * The style of text fields and selects: MUI's outlined, filled or standard
   * (`stroked`, an underline only) field. Unset is `outlined`.
   */
  formFieldStyle?: 'outlined' | 'filled' | 'stroked';
  /**
   * Overrides for the generated MUI palette, merged over the defaults in
   * `generateTheme` one color at a time. `mode` is ignored — the active color
   * scheme decides that. `primary` / `secondary` set here win over the
   * `primaryColor` / `secondaryColor` shorthands, which only cover `main`.
   */
  palette?: ThemeOptions['palette'];
  primaryColor?: string;
  secondaryColor?: string;
  /**
   * The scheme this theme describes. Unset is `light`. On `theme` it is read
   * only when `darkTheme` is empty: a theme with dark overrides is always
   * light, with the dark scheme on top. On `darkTheme` it marks the dark
   * scheme as present even when it overrides nothing (see `hasDarkScheme`).
   */
  colorScheme?: 'light' | 'dark';
};